import { useState, useEffect, useRef } from 'react';
import { Question } from '../types/game';
import { getQuestionId } from '../utils/gameUtils';
import { FLIP_DELAY } from '../constants/gameConstants';

interface UseQuestionFlipProps {
  currentQuestion: Question | null;
  liveAnswers: Array<{ playerId: string }>;
  totalPlayers: number;
  answerSubmitted: boolean;
  hostIsPlaying: boolean;
  onQuestionChange?: (question: Question | null) => void;
}

interface UseQuestionFlipReturn {
  showQuestionFlip: boolean;
  setShowQuestionFlip: React.Dispatch<React.SetStateAction<boolean>>;
  isFadingOut: boolean;
  previousQuestionIdForFlipRef: React.MutableRefObject<string | undefined>;
}

/**
 * Hook to manage question flip animation (flip to "Did you know?" when all players answer or timer runs out).
 * When the next question arrives, snap back to the front immediately — do not keep the card flipped
 * through a fade (that blanks the host while players already see answers).
 */
export const useQuestionFlip = ({
  currentQuestion,
  liveAnswers,
  totalPlayers,
  answerSubmitted,
  hostIsPlaying,
}: UseQuestionFlipProps): UseQuestionFlipReturn => {
  const [showQuestionFlip, setShowQuestionFlip] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const previousQuestionIdForFlipRef = useRef<string | undefined>(undefined);

  // New question: show front immediately (players already have options; host must not lag).
  useEffect(() => {
    if (!currentQuestion) return;
    const questionId = getQuestionId(currentQuestion);
    if (questionId === previousQuestionIdForFlipRef.current) return;

    previousQuestionIdForFlipRef.current = questionId;
    setShowQuestionFlip(false);
    setIsFadingOut(false);
  }, [currentQuestion]);

  // Detect when all players have answered to trigger flip
  useEffect(() => {
    if (!currentQuestion || !totalPlayers) return;

    const allAnswered = hostIsPlaying
      ? answerSubmitted && liveAnswers.length >= totalPlayers - 1
      : liveAnswers.length >= totalPlayers;

    if (allAnswered && currentQuestion.interesting && !showQuestionFlip) {
      const currentQuestionId = getQuestionId(currentQuestion);
      previousQuestionIdForFlipRef.current = currentQuestionId;
      const t = setTimeout(() => {
        setShowQuestionFlip(true);
      }, FLIP_DELAY);
      return () => clearTimeout(t);
    }
  }, [liveAnswers, totalPlayers, currentQuestion, answerSubmitted, hostIsPlaying, showQuestionFlip]);

  return {
    showQuestionFlip,
    setShowQuestionFlip,
    isFadingOut,
    previousQuestionIdForFlipRef,
  };
};

/**
 * Helper function to trigger flip when timer runs out
 * This is used externally when timer reaches 0
 */
export const useTimerFlipTrigger = (
  timeRemaining: number,
  currentQuestion: Question | null,
  showQuestionFlip: boolean,
  setShowQuestionFlip: React.Dispatch<React.SetStateAction<boolean>>,
  previousQuestionIdForFlipRef: React.MutableRefObject<string | undefined>
) => {
  useEffect(() => {
    if (timeRemaining === 0 && currentQuestion?.interesting && !showQuestionFlip) {
      const currentQuestionId = getQuestionId(currentQuestion);
      previousQuestionIdForFlipRef.current = currentQuestionId;
      const t = setTimeout(() => {
        setShowQuestionFlip(true);
      }, FLIP_DELAY);
      return () => clearTimeout(t);
    }
  }, [timeRemaining, currentQuestion, showQuestionFlip, setShowQuestionFlip, previousQuestionIdForFlipRef]);
};
